import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, fmtUSD, SITUATIONS } from "@/lib/api";
import { Search } from "lucide-react";

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ city: "", state: "", situation: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.city) params.city = filters.city;
      if (filters.state) params.state = filters.state;
      if (filters.situation) params.situation = filters.situation;
      const { data } = await api.get("/properties/marketplace", { params });
      setItems(data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <DashboardLayout>
      <p className="eyebrow">Marketplace</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Opportunities</h1>

      <div className="mt-6 bg-white border border-[var(--adn-line)] p-4 flex flex-wrap gap-3 items-end">
        <Filter k="City"><input className="op-input" value={filters.city} onChange={(e)=>setFilters({...filters, city: e.target.value})} placeholder="e.g. Dallas" /></Filter>
        <Filter k="State"><input className="op-input" value={filters.state} onChange={(e)=>setFilters({...filters, state: e.target.value})} placeholder="TX" maxLength={2} /></Filter>
        <Filter k="Situation">
          <select className="op-input" value={filters.situation} onChange={(e)=>setFilters({...filters, situation: e.target.value})}>
            <option value="">Any</option>
            {SITUATIONS.map((s)=><option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Filter>
        <button data-testid="opp-search" onClick={load} className="btn-gold px-5 py-2.5 rounded text-sm inline-flex items-center gap-2">
          <Search size={14} /> Search
        </button>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-[var(--adn-text-soft)]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="mt-10 text-sm text-[var(--adn-text-soft)]">No matches. Try expanding your filters.</p>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <Link to={`/properties/${p.property_id}`} key={p.property_id} data-testid={`opp-${p.property_id}`} className="bg-white border border-[var(--adn-line)] p-6 card-hover">
              <div className="flex justify-between">
                <span className="font-display font-semibold">{p.city}, {p.state}</span>
                <span className="text-xs text-[var(--adn-text-muted)]">{p.zip_code}</span>
              </div>
              <div className="mt-2 text-sm text-[var(--adn-text-soft)]">{p.property_type} · {p.bedrooms || "—"}bd / {p.bathrooms || "—"}ba</div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(p.situations || []).slice(0, 3).map((s) => {
                  const meta = SITUATIONS.find(x => x.id === s);
                  return <span key={s} className={`pill ${meta?.pill || "pill-neutral"}`}>{meta?.label || s}</span>;
                })}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                <Stat label="Value" v={fmtUSD(p.estimated_value)} />
                <Stat label="Asking" v={fmtUSD(p.asking_price)} />
                <Stat label="Distress" v={p.ai?.distress_score ?? "—"} accent />
              </div>
            </Link>
          ))}
        </div>
      )}
      <style>{`.op-input { background: white; border: 1px solid #CBD5E1; padding: 8px 10px; font-size: 13px; min-width: 140px; } .op-input:focus { outline: none; border-color: var(--adn-navy); }`}</style>
    </DashboardLayout>
  );
}
function Filter({ k, children }) { return <label className="flex flex-col text-xs text-[var(--adn-text-soft)] gap-1.5">{k}{children}</label>; }
function Stat({ label, v, accent }) { return <div><div className="text-[10px] uppercase tracking-[0.15em] text-[var(--adn-text-muted)]">{label}</div><div className={`font-display tabular ${accent ? "text-[var(--adn-gold)]" : ""}`}>{v}</div></div>; }
